import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { useEffect, useState } from 'react';
import {
  useAiClearFromStatement,
  useCompleteReconciliation,
  useDeleteInProgressReconciliation,
  useReconcilableItems,
  useReconciliation,
  useReconciliationWorksheet,
  useSetReconciliationItems,
  useUpdateReconciliation,
  useVerifyChain,
  useVoidReconciliation,
} from '../../hooks/useReconciliation';
import { StatementsPanel } from '../../components/statements/StatementsPanel';
import { ReclassifyLauncher } from '../../components/reconciliation/ReclassifyLauncher';
import { StatementDropZone } from '../../components/reconciliation/StatementDropZone';
import { useStatements } from '../../hooks/useStatements';
import type { AiClearResult, AiClearSuggestion, BankStatement } from '@accounting/shared';
import { useActiveCompanyId } from '../../stores/useCompanyStore';
import { ApiError, fetchBlobObjectUrl, openFileInNewTab } from '../../lib/api';
import { formatAccountingDate, formatCurrency } from '../../lib/formatters';

export const Route = createFileRoute('/_auth/reconcile/$id/')({
  component: ReconcileDetail,
});

function apiErrorMessage(err: unknown): string | undefined {
  if (err instanceof ApiError) {
    return (err.body as { error?: string } | null)?.error ?? 'Request failed';
  }
  return err instanceof Error ? err.message : undefined;
}

function ReconcileDetail() {
  const { id } = Route.useParams();
  const companyId = useActiveCompanyId();
  const { data: recon, isLoading, error } = useReconciliation(companyId, id);

  if (!companyId) {
    return (
      <div className="card" style={{ textAlign: 'center', padding: 32 }}>
        <p className="muted">No active company.</p>
      </div>
    );
  }

  if (isLoading) return <p className="muted">Loading…</p>;
  if (error || !recon) return <p className="field-error">Failed to load reconciliation.</p>;

  const accountName = recon.glAccountName
    ? `${recon.glAccountCode ? recon.glAccountCode + ' · ' : ''}${recon.glAccountName}`
    : 'Account';

  return (
    <div>
      <Link to="/reconcile" style={{ fontSize: 13 }}>
        ← All reconciliations
      </Link>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', margin: '8px 0 16px' }}>
        <div>
          <h1 style={{ fontSize: 24, margin: 0 }}>
            {accountName} — {formatAccountingDate(recon.statementDate)}
          </h1>
          <p className="muted" style={{ margin: '4px 0 0', fontSize: 13 }}>
            Beginning {formatCurrency(recon.statementBeginningBalance)} · Statement ending{' '}
            {formatCurrency(recon.statementEndingBalance)}
          </p>
        </div>
        <StatusBadge status={recon.effectiveStatus} />
      </div>

      {/* Statements come BEFORE the checkoff workflow so the user has the PDF
          in hand (or knows it's missing) before starting to tick items. */}
      <StatementsPanel
        companyId={companyId}
        bankAccountId={recon.bankAccountId}
        reconciliationId={recon.id}
        defaultStatementDate={recon.statementDate}
        defaultEndingBalance={recon.statementEndingBalance}
      />

      {recon.effectiveStatus === 'in_progress' && (
        <InProgressSession
          companyId={companyId}
          reconId={id}
          recon={recon}
        />
      )}

      {recon.effectiveStatus === 'completed' && (
        <CompletedSession companyId={companyId} reconId={id} recon={recon} />
      )}

      {recon.effectiveStatus === 'voided' && (
        <div className="card" style={{ padding: 20 }}>
          <p style={{ marginTop: 0 }}>
            This reconciliation was <strong>voided</strong>. The journal entries it locked have
            been released and its bank transactions returned to the matched queue.
          </p>
          {recon.voidReason && (
            <p className="muted" style={{ marginBottom: 0 }}>Reason: {recon.voidReason}</p>
          )}
        </div>
      )}

      <ReconcileWorksheet companyId={companyId} bankAccountId={recon.bankAccountId} />

      <ChainStatus companyId={companyId} bankAccountId={recon.bankAccountId} />
    </div>
  );
}

function ReconcileWorksheet({
  companyId,
  bankAccountId,
}: {
  companyId: string;
  bankAccountId: string;
}) {
  const { data: ws, isLoading } = useReconciliationWorksheet(companyId, bankAccountId);
  if (isLoading || !ws) return null;

  const hasAnything =
    ws.unclearedChecks.length > 0 ||
    ws.depositsInTransit.length > 0 ||
    ws.reconciledCount > 0 ||
    parseFloat(ws.undepositedFundsBalance) !== 0;
  if (!hasAnything) return null;

  return (
    <div className="card" style={{ marginTop: 16, padding: 16 }}>
      <h2 style={{ fontSize: 16, marginTop: 0 }}>Worksheet</h2>
      <p className="muted" style={{ marginTop: 0, fontSize: 13 }}>
        Items that explain the gap between your books and the bank statement for{' '}
        {ws.glAccountCode} — {ws.glAccountName}.
      </p>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 16 }}>
        {/* Uncleared (outstanding) checks */}
        <div>
          <h3 style={{ fontSize: 14, margin: '0 0 6px' }}>
            Uncleared checks{' '}
            <span className="muted">({ws.unclearedChecks.length} · {formatCurrency(ws.unclearedChecksTotal)})</span>
          </h3>
          {ws.unclearedChecks.length === 0 ? (
            <p className="muted" style={{ fontSize: 13 }}>None — all checks have cleared.</p>
          ) : (
            <table className="table">
              <tbody>
                {ws.unclearedChecks.map((c) => (
                  <tr key={c.id}>
                    <td>
                      <Link to="/checks/$id" params={{ id: c.id }}>#{c.checkNumber}</Link>{' '}
                      <span className="muted">{c.payeeName}</span>
                      <div className="muted" style={{ fontSize: 12 }}>{formatAccountingDate(c.checkDate)}</div>
                    </td>
                    <td style={{ textAlign: 'right' }}>{formatCurrency(c.amount)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* Undeposited funds / deposits in transit */}
        <div>
          <h3 style={{ fontSize: 14, margin: '0 0 6px' }}>
            Undeposited funds{' '}
            <span className="muted">(deposits in transit {formatCurrency(ws.depositsInTransitTotal)})</span>
          </h3>
          <p className="muted" style={{ fontSize: 12, marginTop: 0 }}>
            Undeposited Funds (1900) balance: <strong>{formatCurrency(ws.undepositedFundsBalance)}</strong>
          </p>
          {ws.depositsInTransit.length === 0 ? (
            <p className="muted" style={{ fontSize: 13 }}>No deposits in transit.</p>
          ) : (
            <table className="table">
              <tbody>
                {ws.depositsInTransit.map((d) => (
                  <tr key={d.id}>
                    <td>
                      <Link to="/deposits/$id" params={{ id: d.id }}>{d.payerName}</Link>
                      <div className="muted" style={{ fontSize: 12 }}>{formatAccountingDate(d.depositDate)}</div>
                    </td>
                    <td style={{ textAlign: 'right' }}>{formatCurrency(d.amount)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* Cleared & reconciled */}
        <div>
          <h3 style={{ fontSize: 14, margin: '0 0 6px' }}>
            Cleared &amp; reconciled{' '}
            <span className="muted">
              ({ws.reconciledCount} txn{ws.reconciledCount === 1 ? '' : 's'},{' '}
              {ws.clearedChecks.length} chk, {ws.clearedDeposits.length} dep)
            </span>
          </h3>
          {ws.reconciledTransactions.length === 0 && ws.clearedChecks.length === 0 && ws.clearedDeposits.length === 0 ? (
            <p className="muted" style={{ fontSize: 13 }}>Nothing reconciled yet.</p>
          ) : (
            <table className="table">
              <tbody>
                {ws.reconciledTransactions.slice(0, 12).map((t) => (
                  <tr key={t.id}>
                    <td>
                      {t.description}
                      <div className="muted" style={{ fontSize: 12 }}>{formatAccountingDate(t.date)}</div>
                    </td>
                    <td style={{ textAlign: 'right' }}>{formatCurrency(t.amount)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}

function StatusBadge({ status }: { status: string }) {
  if (status === 'completed') return <span className="tag ok">completed</span>;
  if (status === 'in_progress') return <span className="tag warn">in progress</span>;
  if (status === 'voided') return <span className="tag bad">voided</span>;
  return <span className="tag">{status}</span>;
}

function InProgressSession({
  companyId,
  reconId,
  recon,
}: {
  companyId: string;
  reconId: string;
  recon: {
    statementDate: string;
    statementBeginningBalance: string;
    statementEndingBalance: string;
    glAccountId?: string | null;
    statementDocument?: { id: string; fileName: string } | null;
  };
}) {
  const navigate = useNavigate();
  const itemsQuery = useReconcilableItems(companyId, reconId);
  const items = itemsQuery.data ?? [];
  const setItems = useSetReconciliationItems(companyId);
  const complete = useCompleteReconciliation(companyId);
  const deleteRecon = useDeleteInProgressReconciliation(companyId);
  const aiClear = useAiClearFromStatement(companyId);
  // Statements linked to this recon — drives the "AI auto-check" picker.
  const { data: linkedStatements = [] } = useStatements(companyId, { reconciliationId: reconId });

  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [initialized, setInitialized] = useState(false);
  const [editing, setEditing] = useState(false);
  const [aiSuggestions, setAiSuggestions] = useState<AiClearResult | null>(null);
  // Entry whose offset account the user is correcting, without leaving the page.
  const [reclassifyEntryId, setReclassifyEntryId] = useState<string | null>(null);
  // Default to "adjacent" — historical experience showed users wading through
  // months of entries because all unreconciled items were always visible.
  // Adjacent = items dated ≤ statementDate + 5 days (covers the statement
  // period plus a small tail for late posts). "all" stays available because
  // sometimes a check from 3 months ago finally clears.
  const [dateScope, setDateScope] = useState<'adjacent' | 'all'>('adjacent');

  useEffect(() => {
    if (itemsQuery.isSuccess && !initialized) {
      setSelected(new Set(items.filter((i) => i.cleared).map((i) => i.journalEntryId)));
      setInitialized(true);
    }
  }, [itemsQuery.isSuccess, items, initialized]);

  function toggle(entryId: string) {
    const next = new Set(selected);
    if (next.has(entryId)) next.delete(entryId);
    else next.add(entryId);
    setSelected(next);
    setItems.mutate({ id: reconId, input: { journalEntryIds: [...next] } });
  }

  const begin = parseFloat(recon.statementBeginningBalance);
  const ending = parseFloat(recon.statementEndingBalance);
  const clearedTotal = items
    .filter((i) => selected.has(i.journalEntryId))
    .reduce((s, i) => s + parseFloat(i.signedMovement), 0);
  const clearedBalance = begin + clearedTotal;
  const difference = ending - clearedBalance;
  const balanced = Math.abs(difference) < 0.005;

  // Statement-adjacent cutoff = statement date + 5 days. Anything dated up to
  // and including that day is in scope. Earlier dates (older stragglers that
  // haven't cleared) are naturally included since they're earlier.
  const adjacentCutoff = (() => {
    const [y, m, d] = recon.statementDate.split('-').map(Number);
    const dt = new Date(Date.UTC(y!, m! - 1, d! + 5));
    return dt.toISOString().slice(0, 10);
  })();
  const visibleItems =
    dateScope === 'all'
      ? items
      : items.filter((i) => i.date <= adjacentCutoff);
  const hiddenSelectedCount = items.filter(
    (i) => selected.has(i.journalEntryId) && !visibleItems.some((v) => v.journalEntryId === i.journalEntryId),
  ).length;

  const completeError = apiErrorMessage(complete.error);

  return (
    <>
      <div
        className="card"
        style={{
          display: 'flex',
          gap: 24,
          flexWrap: 'wrap',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: 16,
          padding: 16,
        }}
      >
        <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap' }}>
          <Figure label="Cleared balance" value={formatCurrency(clearedBalance)} />
          <Figure label="Statement ending" value={formatCurrency(ending)} />
          <Figure
            label="Difference"
            value={formatCurrency(difference, true)}
            emphasis={balanced ? 'ok' : 'bad'}
          />
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8, minWidth: 240 }}>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <button
              type="button"
              className="btn-secondary"
              onClick={() => setEditing((v) => !v)}
            >
              {editing ? 'Close' : 'Edit statement'}
            </button>
            {linkedStatements.length === 0 && !recon.statementDocument && (
              // One drop = stored, filed under the month it covers, linked to
              // this reconciliation, and read by the AI.
              <StatementDropZone
                companyId={companyId}
                reconId={reconId}
                busyLabel={aiClear.isPending ? 'Reading statement…' : null}
                idleLabel="📄 Drop the bank statement here (or click) — it files itself and auto-checks"
                onAttached={(r) =>
                  aiClear.mutate(
                    { id: reconId, statementId: r.statementId },
                    { onSuccess: (res) => setAiSuggestions(res) },
                  )
                }
              />
            )}
            {linkedStatements.length === 0 && recon.statementDocument && (
              <button
                type="button"
                className="btn-secondary"
                disabled={aiClear.isPending}
                title={`Use ${recon.statementDocument.fileName} to suggest cleared items`}
                onClick={() =>
                  aiClear.mutate(
                    { id: reconId, statementId: recon.statementDocument!.id },
                    { onSuccess: (r) => setAiSuggestions(r) },
                  )
                }
              >
                {aiClear.isPending ? 'Analyzing…' : '🤖 AI auto-check'}
              </button>
            )}
            {linkedStatements.length > 0 && (
              <button
                type="button"
                className="btn-secondary"
                disabled={aiClear.isPending}
                title={
                  linkedStatements.length === 1
                    ? `Use ${linkedStatements[0]!.fileName} to suggest cleared items`
                    : `Use ${linkedStatements[linkedStatements.length - 1]!.fileName} to suggest cleared items`
                }
                onClick={() => {
                  // Use the most-recently-uploaded statement (the list is sorted desc by date+createdAt).
                  const stmt = linkedStatements[0]!;
                  aiClear.mutate(
                    { id: reconId, statementId: stmt.id },
                    { onSuccess: (r) => setAiSuggestions(r) },
                  );
                }}
              >
                {aiClear.isPending ? 'Analyzing…' : '🤖 AI auto-check'}
              </button>
            )}
            <button
              className="btn-primary"
              style={{ flex: 1 }}
              disabled={!balanced}
              title={balanced ? undefined : 'Difference must be $0.00 to complete'}
              onClick={() =>
                void navigate({ to: '/reconcile/$id/preview', params: { id: reconId } })
              }
            >
              Review &amp; complete
            </button>
          </div>
          {aiClear.error && (
            <div className="field-error" style={{ fontSize: 12 }}>
              {apiErrorMessage(aiClear.error) ?? 'AI auto-check failed.'}
            </div>
          )}
          <button
            type="button"
            className="btn-danger"
            disabled={deleteRecon.isPending}
            onClick={() => {
              if (
                confirm(
                  'Delete this in-progress reconciliation? Cleared markers are dropped; uploaded statements stay (just unlinked). No journal entries are affected.',
                )
              ) {
                deleteRecon.mutate(reconId, { onSuccess: () => void navigate({ to: '/reconcile' }) });
              }
            }}
          >
            {deleteRecon.isPending ? 'Deleting…' : 'Delete reconciliation'}
          </button>
        </div>
      </div>

      {aiSuggestions && (
        <AiSuggestionsModal
          result={aiSuggestions}
          statement={linkedStatements[0]}
          onCancel={() => setAiSuggestions(null)}
          onApply={(approvedIds) => {
            // Union the approved IDs with the already-checked items, then submit.
            const merged = new Set(selected);
            for (const id of approvedIds) merged.add(id);
            setSelected(merged);
            setItems.mutate(
              { id: reconId, input: { journalEntryIds: [...merged] } },
              { onSuccess: () => setAiSuggestions(null) },
            );
          }}
        />
      )}

      {editing && (
        <EditStatementForm
          companyId={companyId}
          reconId={reconId}
          recon={recon}
          onClose={() => setEditing(false)}
        />
      )}

      {completeError && <div className="field-error" style={{ marginBottom: 12 }}>{completeError}</div>}

      <p className="muted" style={{ fontSize: 13 }}>
        Check off each entry that appears on your statement. The difference reaches $0.00 when the
        cleared balance matches the statement ending balance.
      </p>

      {itemsQuery.isLoading && <p className="muted">Loading entries…</p>}
      {itemsQuery.isSuccess && items.length === 0 && (
        <div className="card" style={{ textAlign: 'center', padding: 24 }}>
          <p className="muted" style={{ marginTop: 0 }}>
            No unreconciled entries are posted to this account yet. Post entries (bills, checks,
            deposits, journal entries) or match bank-feed transactions, and they'll appear here.
          </p>
          <Link to="/bank/transactions" className="btn-secondary" style={{ textDecoration: 'none' }}>
            Bank transactions
          </Link>
        </div>
      )}

      {items.length > 0 && (
        <>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 12,
              fontSize: 13,
              padding: '6px 0 10px',
              flexWrap: 'wrap',
            }}
          >
            <span className="muted">
              {selected.size} of {items.length} selected
              {dateScope === 'adjacent' && visibleItems.length !== items.length && (
                <> · showing {visibleItems.length} adjacent</>
              )}
              {hiddenSelectedCount > 0 && (
                <> · {hiddenSelectedCount} selected hidden by filter</>
              )}
            </span>
            <div style={{ display: 'inline-flex', gap: 0, border: '1px solid var(--border)', borderRadius: 4, overflow: 'hidden' }}>
              <button
                type="button"
                className={dateScope === 'adjacent' ? 'btn-primary' : 'btn-secondary'}
                style={{ padding: '4px 10px', fontSize: 12, borderRadius: 0 }}
                onClick={() => setDateScope('adjacent')}
                title={`Show entries dated up to ${formatAccountingDate(adjacentCutoff)} (statement date + 5 days). Older unreconciled stragglers also included.`}
              >
                Statement adjacent
              </button>
              <button
                type="button"
                className={dateScope === 'all' ? 'btn-primary' : 'btn-secondary'}
                style={{ padding: '4px 10px', fontSize: 12, borderRadius: 0 }}
                onClick={() => setDateScope('all')}
                title="Show every unreconciled entry posted to this account."
              >
                All dates
              </button>
            </div>
            <button
              type="button"
              className="btn-secondary"
              style={{ padding: '4px 10px', fontSize: 12 }}
              disabled={visibleItems
                .filter((i) => i.date <= recon.statementDate)
                .every((i) => selected.has(i.journalEntryId))}
              onClick={() => {
                // Add every visible item to the selection (keeps anything already
                // selected outside the current view — they still count toward the
                // cleared balance).
                //
                // Entries dated AFTER the statement date are skipped: they cannot
                // appear on this statement, so bulk-clearing them produces a
                // difference exactly their size and sends the user hunting for a
                // phantom error. They stay individually tickable for the rare
                // case where the booked date is simply wrong.
                const next = new Set(selected);
                for (const i of visibleItems) {
                  if (i.date > recon.statementDate) continue;
                  next.add(i.journalEntryId);
                }
                setSelected(next);
                setItems.mutate({ id: reconId, input: { journalEntryIds: [...next] } });
              }}
            >
              Check all visible
            </button>
            {visibleItems.some((i) => i.date > recon.statementDate) && (
              <span className="muted" style={{ fontSize: 11 }}>
                {visibleItems.filter((i) => i.date > recon.statementDate).length} dated after{' '}
                {recon.statementDate} — not checked, they can&rsquo;t be on this statement
              </span>
            )}
            <button
              type="button"
              className="btn-secondary"
              style={{ padding: '4px 10px', fontSize: 12 }}
              disabled={!visibleItems.some((i) => selected.has(i.journalEntryId))}
              onClick={() => {
                const next = new Set(selected);
                for (const i of visibleItems) next.delete(i.journalEntryId);
                setSelected(next);
                setItems.mutate({ id: reconId, input: { journalEntryIds: [...next] } });
              }}
            >
              Uncheck all visible
            </button>
          </div>
          <table className="table">
            <thead>
              <tr>
                <th style={{ width: 40 }}>
                  <input
                    type="checkbox"
                    aria-label="Toggle all visible items"
                    checked={
                      visibleItems.length > 0 &&
                      visibleItems.every((i) => selected.has(i.journalEntryId))
                    }
                    ref={(el) => {
                      if (!el) return;
                      const visibleSelected = visibleItems.filter((i) => selected.has(i.journalEntryId)).length;
                      el.indeterminate = visibleSelected > 0 && visibleSelected < visibleItems.length;
                    }}
                    onChange={(e) => {
                      const next = new Set(selected);
                      if (e.target.checked) {
                        for (const i of visibleItems) next.add(i.journalEntryId);
                      } else {
                        for (const i of visibleItems) next.delete(i.journalEntryId);
                      }
                      setSelected(next);
                      setItems.mutate({ id: reconId, input: { journalEntryIds: [...next] } });
                    }}
                  />
                </th>
                <th>Date</th>
                <th>Entry</th>
                <th>Description</th>
                <th style={{ textAlign: 'right' }}>Cleared effect</th>
                <th style={{ width: 1 }}></th>
              </tr>
            </thead>
            <tbody>
              {visibleItems.map((i) => {
                const checked = selected.has(i.journalEntryId);
                return (
                  <tr
                    key={i.journalEntryId}
                    style={{ cursor: 'pointer' }}
                    onClick={() => toggle(i.journalEntryId)}
                  >
                    <td onClick={(e) => e.stopPropagation()}>
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={() => toggle(i.journalEntryId)}
                      />
                    </td>
                    <td>{formatAccountingDate(i.date)}</td>
                    <td style={{ fontFamily: 'ui-monospace, monospace', fontSize: 13 }}>
                      <Link to="/journal/$id" params={{ id: i.journalEntryId }} onClick={(e) => e.stopPropagation()}>
                        {i.entryNumber}
                      </Link>
                    </td>
                    <td>{i.description}</td>
                    <td style={{ textAlign: 'right' }}>{formatCurrency(i.signedMovement, true)}</td>
                    <td onClick={(e) => e.stopPropagation()}>
                      <button
                        type="button"
                        className="btn-secondary"
                        style={{ padding: '2px 8px', fontSize: 12, whiteSpace: 'nowrap' }}
                        onClick={() => setReclassifyEntryId(i.journalEntryId)}
                        title="Change which account the other side of this entry posts to, or split it — the bank line stays exactly as it is"
                      >
                        Recategorise
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </>
      )}

      {reclassifyEntryId && (
        <ReclassifyLauncher
          companyId={companyId}
          entryId={reclassifyEntryId}
          // Freeze the line on the account being reconciled: the statement proves
          // that movement, so only the offset is up for discussion.
          frozenAccountId={recon.glAccountId ?? null}
          onClose={() => setReclassifyEntryId(null)}
        />
      )}
    </>
  );
}

function EditStatementForm({
  companyId,
  reconId,
  recon,
  onClose,
}: {
  companyId: string;
  reconId: string;
  recon: {
    statementDate: string;
    statementBeginningBalance: string;
    statementEndingBalance: string;
  };
  onClose: () => void;
}) {
  const update = useUpdateReconciliation(companyId);
  const [form, setForm] = useState({
    statementDate: recon.statementDate,
    statementBeginningBalance: recon.statementBeginningBalance,
    statementEndingBalance: recon.statementEndingBalance,
  });
  const errorMessage = apiErrorMessage(update.error);

  return (
    <form
      className="card"
      style={{ marginBottom: 16 }}
      onSubmit={(e) => {
        e.preventDefault();
        update.mutate(
          {
            id: reconId,
            input: {
              statementDate: form.statementDate,
              statementBeginningBalance: form.statementBeginningBalance.trim() || '0',
              statementEndingBalance: form.statementEndingBalance.trim(),
            },
          },
          { onSuccess: () => onClose() },
        );
      }}
    >
      <h2 style={{ marginTop: 0, fontSize: 18 }}>Edit statement details</h2>
      <div className="form-grid">
        <label htmlFor="esd">Statement date</label>
        <input
          id="esd"
          type="date"
          value={form.statementDate}
          onChange={(e) => setForm((p) => ({ ...p, statementDate: e.target.value }))}
          required
        />

        <label htmlFor="ebeg">Beginning balance</label>
        <input
          id="ebeg"
          type="text"
          inputMode="decimal"
          value={form.statementBeginningBalance}
          onChange={(e) => setForm((p) => ({ ...p, statementBeginningBalance: e.target.value }))}
          style={{ textAlign: 'right', maxWidth: 160 }}
        />

        <label htmlFor="eend">Ending balance</label>
        <input
          id="eend"
          type="text"
          inputMode="decimal"
          value={form.statementEndingBalance}
          onChange={(e) => setForm((p) => ({ ...p, statementEndingBalance: e.target.value }))}
          style={{ textAlign: 'right', maxWidth: 160 }}
          required
        />
      </div>
      {errorMessage && <div className="field-error" style={{ marginTop: 12 }}>{errorMessage}</div>}
      <div style={{ display: 'flex', gap: 8, marginTop: 16 }}>
        <button
          type="submit"
          className="btn-primary"
          disabled={update.isPending || !form.statementEndingBalance.trim()}
        >
          {update.isPending ? 'Saving…' : 'Save'}
        </button>
        <button type="button" className="btn-secondary" onClick={onClose}>
          Cancel
        </button>
      </div>
    </form>
  );
}

function CompletedSession({
  companyId,
  reconId,
  recon,
}: {
  companyId: string;
  reconId: string;
  recon: {
    clearedBalance: string | null;
    completedAt: string | null;
    statementDocument?: { id: string; fileName: string } | null;
  };
}) {
  const voidRecon = useVoidReconciliation(companyId);
  const voidError = apiErrorMessage(voidRecon.error);

  return (
    <div className="card" style={{ padding: 20, marginBottom: 16 }}>
      <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap', justifyContent: 'space-between' }}>
        <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap' }}>
          <Figure
            label="Cleared balance"
            value={recon.clearedBalance ? formatCurrency(recon.clearedBalance) : '—'}
          />
          <Figure label="Difference" value={formatCurrency('0', true)} emphasis="ok" />
          {recon.completedAt && (
            <Figure label="Completed" value={new Date(recon.completedAt).toLocaleString()} />
          )}
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start', flexWrap: 'wrap' }}>
          {/* The evidence behind the proof. Completing is append-only, but the
              statement link lives on the vault row, not on the reconciliation
              the hash chain signs, so a reconciliation finished without its
              PDF can still be made whole here — and must be, or the month-end
              packet goes out thin. */}
          {recon.statementDocument ? (
            <button
              type="button"
              className="btn-secondary"
              title={recon.statementDocument.fileName}
              onClick={() =>
                openFileInNewTab(
                  `/companies/${companyId}/vault/documents/${recon.statementDocument!.id}/download`,
                )
              }
            >
              📄 Statement on file
            </button>
          ) : (
            <StatementDropZone
              companyId={companyId}
              reconId={reconId}
              idleLabel="⚠️ No statement on file — drop the bank statement here (or click)"
            />
          )}
          <button
            type="button"
            className="btn-secondary"
            onClick={() =>
              openFileInNewTab(`/companies/${companyId}/reconciliations/${reconId}/report.pdf`)
            }
          >
            View report
          </button>
          <button
            className="btn-danger"
            disabled={voidRecon.isPending}
            onClick={() => {
              const reason = prompt('Reason for voiding this reconciliation?');
              if (reason && reason.trim()) {
                voidRecon.mutate({ id: reconId, reason: reason.trim() });
              }
            }}
          >
            {voidRecon.isPending ? 'Voiding…' : 'Void reconciliation'}
          </button>
        </div>
      </div>
      <p className="muted" style={{ fontSize: 13, marginBottom: 0, marginTop: 16 }}>
        Completed reconciliations are append-only. Voiding inserts a new record (the original is
        preserved), unlocks the matched journal entries, and returns the transactions to the
        matched queue.
      </p>
      {voidError && <div className="field-error" style={{ marginTop: 12 }}>{voidError}</div>}
    </div>
  );
}

function ChainStatus({
  companyId,
  bankAccountId,
}: {
  companyId: string;
  bankAccountId: string;
}) {
  const { data, isLoading } = useVerifyChain(companyId, bankAccountId);
  if (isLoading || !data) return null;
  return (
    <div style={{ marginTop: 16, fontSize: 13 }}>
      {data.valid ? (
        <span className="tag ok">hash chain verified</span>
      ) : (
        <span className="tag bad">hash chain TAMPERED</span>
      )}
      <span className="muted" style={{ marginLeft: 8 }}>
        {data.entries.length} completed reconciliation{data.entries.length === 1 ? '' : 's'} in this
        account's chain
      </span>
    </div>
  );
}

function Figure({
  label,
  value,
  emphasis,
}: {
  label: string;
  value: string;
  emphasis?: 'ok' | 'bad';
}) {
  const color =
    emphasis === 'ok' ? 'var(--ok, #1A6B3A)' : emphasis === 'bad' ? 'var(--bad, #C23B22)' : undefined;
  return (
    <div>
      <div className="muted" style={{ fontSize: 12 }}>{label}</div>
      <div style={{ fontSize: 18, fontWeight: 600, color }}>{value}</div>
    </div>
  );
}

function AiSuggestionsModal({
  result,
  statement,
  onCancel,
  onApply,
}: {
  result: AiClearResult;
  statement: BankStatement | undefined;
  onCancel: () => void;
  onApply: (approvedIds: string[]) => void;
}) {
  // Default: every suggestion is checked. The user unchecks any they disagree with.
  const [approved, setApproved] = useState<Set<string>>(
    () => new Set(result.suggestions.map((s) => s.journalEntryId)),
  );

  const toggle = (id: string) =>
    setApproved((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const confidenceBadge = (c: number) => {
    const pct = Math.round(c * 100);
    const cls = c >= 0.9 ? 'tag ok' : c >= 0.75 ? 'tag' : 'tag warn';
    return <span className={cls} style={{ fontSize: 11 }}>{pct}%</span>;
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,0.5)',
        zIndex: 50,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 24,
      }}
      onClick={onCancel}
    >
      <div
        className="card"
        style={{ width: 'min(720px, 96vw)', maxHeight: '92vh', display: 'flex', flexDirection: 'column', padding: 16 }}
        onClick={(e) => e.stopPropagation()}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
          <h2 style={{ margin: 0, fontSize: 18 }}>🤖 AI suggested matches</h2>
          <button type="button" className="btn-secondary" onClick={onCancel}>
            Close
          </button>
        </div>
        <p className="muted" style={{ marginTop: 0, fontSize: 13 }}>
          {statement
            ? `Read from "${statement.fileName}". Review the matches; uncheck anything that's wrong.`
            : 'Review the matches; uncheck anything that’s wrong.'}
          {' '}Applying adds them to the already-cleared set.
        </p>

        {result.parseError && (
          <div className="field-error" style={{ marginBottom: 8 }}>{result.parseError}</div>
        )}

        {result.suggestions.length === 0 ? (
          <p className="muted" style={{ padding: 12 }}>
            The model didn’t find any high-confidence matches in this statement. You can still
            check items manually, or try a clearer statement.
          </p>
        ) : (
          <div style={{ overflowY: 'auto', flex: 1 }}>
            <table className="table">
              <thead>
                <tr>
                  <th style={{ width: 1 }}>
                    <input
                      type="checkbox"
                      checked={approved.size === result.suggestions.length}
                      onChange={(e) =>
                        setApproved(
                          e.target.checked
                            ? new Set(result.suggestions.map((s) => s.journalEntryId))
                            : new Set(),
                        )
                      }
                    />
                  </th>
                  <th>Entry</th>
                  <th>Date</th>
                  <th style={{ textAlign: 'right' }}>Amount</th>
                  <th>Description</th>
                  <th style={{ width: 60 }}>Confidence</th>
                </tr>
              </thead>
              <tbody>
                {result.suggestions.map((s: AiClearSuggestion) => (
                  <tr key={s.journalEntryId}>
                    <td>
                      <input
                        type="checkbox"
                        checked={approved.has(s.journalEntryId)}
                        onChange={() => toggle(s.journalEntryId)}
                      />
                    </td>
                    <td>{s.entryNumber}</td>
                    <td>{s.date}</td>
                    <td style={{ textAlign: 'right' }}>{s.signedAmount}</td>
                    <td title={s.reasoning}>{s.description}</td>
                    <td>{confidenceBadge(s.confidence)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {result.unmatchedStatementLines.length > 0 && (
          <details style={{ marginTop: 10, fontSize: 12 }}>
            <summary className="muted">
              {result.unmatchedStatementLines.length} statement line{result.unmatchedStatementLines.length === 1 ? '' : 's'} the AI couldn’t match to a candidate (review manually)
            </summary>
            <ul style={{ marginTop: 4 }}>
              {result.unmatchedStatementLines.map((l, i) => <li key={i}>{l}</li>)}
            </ul>
          </details>
        )}

        <div style={{ display: 'flex', gap: 8, marginTop: 16, justifyContent: 'flex-end' }}>
          <button type="button" className="btn-secondary" onClick={onCancel}>
            Cancel
          </button>
          <button
            type="button"
            className="btn-primary"
            disabled={approved.size === 0}
            onClick={() => onApply([...approved])}
          >
            Apply {approved.size} match{approved.size === 1 ? '' : 'es'}
          </button>
        </div>
      </div>
    </div>
  );
}
